/**
 * DecisionFlow — CompensationPackageBlueprint
 * ui/src/components/admin/CompensationPackageBlueprint.jsx
 *
 * §10 Compensation Package Blueprint — read-only visual tree
 *
 * Spec authority: comp_policies_spec_s539.md §10 | mastercontext_s549.md P1
 * Session: S549
 *
 * Mount point: PlanDetailView.jsx — rendered after modifier card grid,
 *   when no modifier card is active (standalone section, not a tab).
 *
 * API:
 *   GET /api/v1/compensation/plans/{plan_id}/blueprint
 *
 * Props:
 *   planId        — integer plan ID
 *   period        — { fiscal_period_id, ... } | null  (passed from PlanDetailView)
 *
 * Layout:
 *   - Section header with summary pills (total / approved / pending / draft /
 *     open amendments)
 *   - Per-component cards: label, OTE, approval status dot, modifier chips
 *   - No edit controls — read-only (§10 blueprint is observation surface)
 *   - No mock data. Skeleton while loading. Error state on failure.
 *
 * Constraints:
 *   NN-03: No AI in display logic
 *   NN-35: All FK labels resolved server-side (component_label from API)
 *   No P4-xx codes on any user-facing surface
 *   CSS tokens only: rgb(var(--color-*))
 *   Full file — complete drop-in
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet } from '../../api/client';

// ---------------------------------------------------------------------------
// Approval status styles — matches STATE_STYLE in PlanDetailView.jsx
// ---------------------------------------------------------------------------

const APPROVAL_STYLE = {
  approved:         { dot: 'bg-[rgb(var(--color-success))]',  label: 'Approved',         text: 'text-[rgb(var(--color-success))]'    },
  pending_approval: { dot: 'bg-[rgb(var(--color-info))]',     label: 'Pending Approval', text: 'text-[rgb(var(--color-info))]'       },
  draft:            { dot: 'bg-[rgb(var(--color-warning))]',  label: 'Draft',            text: 'text-[rgb(var(--color-warning))]'    },
};

const MODIFIER_STATUS_STYLE = {
  approved:         'border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/5 text-[rgb(var(--color-success))]',
  pending_approval: 'border-[rgb(var(--color-info))]/40 bg-[rgb(var(--color-info))]/5 text-[rgb(var(--color-info))]',
  not_started:      'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  draft:            'border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/5 text-[rgb(var(--color-warning))]',
};

function modifierStatusStyle(status) {
  return MODIFIER_STATUS_STYLE[status] ?? MODIFIER_STATUS_STYLE.not_started;
}

function pct(v) {
  if (v == null) return '—';
  return `${(parseFloat(v) * 100).toFixed(0)}%`;
}

function currency(val, code) {
  if (val == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code ?? 'USD', maximumFractionDigits: 0 }).format(val);
  } catch {
    return `${code ?? ''} ${val}`;
  }
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function BlueprintSkeleton() {
  return (
    <div className="animate-pulse space-y-3" aria-label="Loading blueprint…">
      <div className="flex gap-2">
        {[80, 120, 100, 110].map((w, i) => (
          <div key={i} className="h-7 rounded-full bg-[rgb(var(--color-surface-muted))]" style={{ width: w }} />
        ))}
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-2.5 w-2.5 rounded-full bg-[rgb(var(--color-surface-muted))]" />
            <div className="h-4 w-48 rounded bg-[rgb(var(--color-surface-muted))]" />
            <div className="ml-auto h-4 w-24 rounded bg-[rgb(var(--color-surface-muted))]" />
          </div>
          <div className="flex gap-2">
            {[60, 90, 75].map((w, j) => (
              <div key={j} className="h-5 rounded-full bg-[rgb(var(--color-surface-muted))]" style={{ width: w }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SummaryPill
// ---------------------------------------------------------------------------

function SummaryPill({ label, count, color }) {
  const colorMap = {
    success: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] border-[rgb(var(--color-success))]/30',
    warning: 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))] border-[rgb(var(--color-warning))]/30',
    info:    'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))] border-[rgb(var(--color-info))]/30',
    danger:  'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))] border-[rgb(var(--color-danger))]/30',
    muted:   'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] border-[rgb(var(--color-border))]',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-tok-xs font-medium ${colorMap[color] ?? colorMap.muted}`}>
      {count != null && <span className="font-semibold">{count}</span>}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ComponentCard
// ---------------------------------------------------------------------------

function ComponentCard({ component }) {
  const [expanded, setExpanded] = useState(false);
  const apprStyle = APPROVAL_STYLE[component.approval_status] ?? APPROVAL_STYLE.draft;

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      {/* Card header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[rgb(var(--color-surface-muted))] transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[rgb(var(--color-focus-ring))]"
        aria-expanded={expanded}
      >
        {/* Approval dot */}
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${apprStyle.dot}`} title={apprStyle.label} />

        {/* Component label */}
        <p className="flex-1 text-tok-sm font-medium text-[rgb(var(--color-text))] truncate">
          {component.component_label}
        </p>

        {/* OTE midpoint */}
        {component.ote_midpoint != null && (
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))] shrink-0">
            {currency(component.ote_midpoint, component.ote_currency_code)} OTE
          </span>
        )}

        {/* Open amendment badge */}
        {component.has_open_amendment && (
          <span className="shrink-0 rounded-full border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/5 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
            Amendment Open
          </span>
        )}

        {/* Approval status */}
        <span className={`shrink-0 text-tok-xs font-medium ${apprStyle.text}`}>
          {apprStyle.label}
        </span>

        {/* Chevron */}
        <svg
          className={`h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))] transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[rgb(var(--color-border))] px-4 py-4 space-y-4">
          {/* OTE split */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Base</p>
              <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{pct(component.base_pct)}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable</p>
              <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{pct(component.variable_pct)}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">ARR Weight</p>
              <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{pct(component.arr_weight)}</p>
            </div>
          </div>

          {/* Modifier chips */}
          {component.modifiers && component.modifiers.length > 0 && (
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-2">Modifiers</p>
              <div className="flex flex-wrap gap-1.5">
                {component.modifiers.map((mod, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-medium ${modifierStatusStyle(mod.modifier_status)}`}
                    title={`Applicability: ${mod.applicability}`}
                  >
                    {/* Render modifier_type human-readable — strip internal prefix if present */}
                    {mod.modifier_type.replace(/^P3B-/, '').replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {component.modifiers && component.modifiers.length === 0 && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No modifier applicability records.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompensationPackageBlueprint — main export
// ---------------------------------------------------------------------------

export default function CompensationPackageBlueprint({ planId, period }) {
  const { token } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    setError(null);
    setData(null);

    apiGet(`/api/v1/compensation/plans/${planId}/blueprint`)
      .then((res) => { setData(res); setLoading(false); })
      .catch((err) => {
        setError(err?.message || 'Failed to load blueprint.');
        setLoading(false);
      });
  }, [planId, token]);

  // ── Loading ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <section className="mt-8 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Package Blueprint</h2>
        </div>
        <BlueprintSkeleton />
      </section>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Package Blueprint</h2>
        </div>
        <div
          role="alert"
          className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]"
        >
          {error}
        </div>
      </section>
    );
  }

  if (!data) return null;

  const { plan, components, summary } = data;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <section className="mt-8 space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
          Package Blueprint
        </h2>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Read-only · v{plan.version}</p>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-2">
        <SummaryPill label="components" count={summary.total_components} color="muted" />
        {summary.approved_count > 0 && (
          <SummaryPill label="approved" count={summary.approved_count} color="success" />
        )}
        {summary.pending_count > 0 && (
          <SummaryPill label="pending approval" count={summary.pending_count} color="info" />
        )}
        {summary.draft_count > 0 && (
          <SummaryPill label="draft" count={summary.draft_count} color="warning" />
        )}
        {summary.open_amendment_count > 0 && (
          <SummaryPill label="open amendment" count={summary.open_amendment_count} color="danger" />
        )}
      </div>

      {/* No components */}
      {components.length === 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-6 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No active components found for this plan.</p>
        </div>
      )}

      {/* Component cards */}
      <div className="space-y-2">
        {components.map((comp) => (
          <ComponentCard key={comp.plan_component_id} component={comp} />
        ))}
      </div>

      {/* Governance notice — always visible, non-dismissible */}
      <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
        <svg className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Blueprint is a read-only view of plan structure and governance state.
          To make changes, use <strong className="text-[rgb(var(--color-text))]">Create Change</strong> in the plan header.
        </p>
      </div>
    </section>
  );
}
